import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import {
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  Box,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  EmojiObjects as EmojiObjectsIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { toast } from "react-toastify";
import { useLocation, useHistory } from "react-router-dom";

// ─── Styles ─────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  edgeTab: {
    position: "fixed",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 2px 8px 4px",
    background: theme.palette.primary.main,
    color: "#fff",
    borderRadius: "8px 0 0 8px",
    cursor: "pointer",
    userSelect: "none",
    transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out, background 0.2s",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-50%) translateX(-3px)",
    },
  },
  edgeTabOpen: {
    transform: "translateY(-50%) translateX(100%)",
    opacity: 0,
    pointerEvents: "none",
  },
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1250,
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(2px)",
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
    cursor: "pointer",
  },
  backdropOpen: {
    opacity: 1,
    visibility: "visible",
  },
  offCanvasPanel: {
    position: "fixed",
    top: 0,
    right: 0,
    height: "100vh",
    width: 420,
    maxWidth: "100vw",
    zIndex: 1300,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "-8px 0 30px rgba(0, 0, 0, 0.2)",
    transform: "translateX(100%)",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    willChange: "transform",
  },
  panelOpen: {
    transform: "translateX(0)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    minHeight: 64,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 16,
  },
  headerSubtitle: {
    fontSize: 12,
    opacity: 0.85,
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#f5f5f7",
  },
  messageBubble: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: 16,
    fontSize: 14,
    lineHeight: 1.5,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  userMessage: {
    alignSelf: "flex-end",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  assistantMessage: {
    alignSelf: "flex-start",
    backgroundColor: theme.palette.type === "dark" ? "#2a2a3e" : "#fff",
    color: theme.palette.text.primary,
    borderBottomLeftRadius: 4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  inputArea: {
    display: "flex",
    alignItems: "flex-end",
    padding: "12px 16px",
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: 8,
    backgroundColor: theme.palette.background.paper,
  },
  textField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 20,
      fontSize: 14,
    },
  },
  sendButton: {
    color: "#667eea",
    "&:disabled": {
      color: theme.palette.action.disabled,
    },
  },
  typingIndicator: {
    alignSelf: "flex-start",
    padding: "8px 14px",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#2a2a3e" : "#fff",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "#667eea",
    animation: "$bounce 1.4s infinite ease-in-out",
    "&:nth-child(1)": { animationDelay: "0s" },
    "&:nth-child(2)": { animationDelay: "0.2s" },
    "&:nth-child(3)": { animationDelay: "0.4s" },
  },
  "@keyframes bounce": {
    "0%, 80%, 100%": { transform: "scale(0)" },
    "40%": { transform: "scale(1)" },
  },
  toolChip: {
    fontSize: 11,
    height: 22,
    margin: "2px 4px 2px 0",
  },
  imagePreview: {
    maxWidth: 120,
    maxHeight: 80,
    borderRadius: 8,
    marginTop: 4,
  },
  welcomeBox: {
    textAlign: "center",
    padding: "40px 20px",
    opacity: 0.7,
  },
}));

// ─── Page context detection ─────────────────────────────────

// Mapeia a rota real (routes/index.js) para o pageContext enviado ao backend.
// Páginas sem tools próprias no backend caem em "other" (ou num contexto sem
// tool definido) e o assistente orienta usando o SYSTEM_KNOWLEDGE_BASE —
// nunca mais retorna null, o widget deve existir em qualquer tela logada.
const routeToPageContext = (pathname) => {
  if (pathname === "/" || pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname.startsWith("/tickets")) return "tickets";
  if (pathname.startsWith("/schedules")) return "schedules";
  if (pathname.startsWith("/kanban")) return "kanban";
  if (pathname.startsWith("/contacts")) return "contacts";
  if (pathname.startsWith("/tags")) return "tags";
  if (pathname.startsWith("/quick-messages")) return "quick-messages";
  if (pathname.startsWith("/connections")) return "connections";
  if (pathname.startsWith("/users")) return "users";
  if (pathname.startsWith("/queues")) return "queues";
  if (pathname.startsWith("/settings")) return "settings";
  if (pathname.startsWith("/global-config")) return "global-config";
  if (pathname.startsWith("/prompts")) return "prompts";
  if (pathname.startsWith("/ai-profile")) return "ai-profile";
  if (pathname.startsWith("/chats")) return "chats";
  if (pathname.startsWith("/helps")) return "helps";
  if (pathname.startsWith("/campaigns") || pathname.startsWith("/contact-lists") || pathname.startsWith("/phrase-lists")) return "campaigns";
  if (pathname.startsWith("/financeiro") || pathname.startsWith("/products") || pathname.startsWith("/services") || pathname.startsWith("/budgets") || pathname.startsWith("/invoices")) return "financeiro";
  // queue-integration (Flowbuilder/Webhooks) ainda não tem tools próprias;
  // cai em "other" e o guia do sistema já explica onde fica.
  // Qualquer outra tela logada (consórcio, mecânica, campanhas-config,
  // flowbuilder, relatórios, etc.): widget disponível, só orientação.
  return "other";
};

const pageLabels = {
  dashboard: "Dashboard",
  tickets: "Atendimento",
  schedules: "Agendamento",
  kanban: "Kanban",
  contacts: "Agenda",
  tags: "Etiquetas",
  "quick-messages": "Respostas Rápidas",
  connections: "Conexões WhatsApp",
  users: "Usuários",
  queues: "Filas e Setores",
  settings: "Configurações",
  prompts: "Talk AI",
  "ai-profile": "Agente SDR",
  chats: "Chat Interno",
  helps: "Ajuda",
  campaigns: "Campanhas",
  financeiro: "Financeiro",
  "global-config": "Configurações Globais",
  other: "Sistema",
};

// ─── Component ──────────────────────────────────────────────

// Helper: render message content with markdown links as clickable elements
const renderLinks = (text) => {
  if (!text) return null;

  // Split by markdown links [text](url)
  const parts = text.split(/(\[([^\]]+)\]\(([^)]+)\))/g);
  if (parts.length === 1) return text;

  const elements = [];
  let i = 0;
  while (i < parts.length) {
    // parts[i] = text before match
    // parts[i+1] = full match [text](url)
    // parts[i+2] = link text
    // parts[i+3] = url
    if (parts[i] && parts[i].match(/^\[.+\]\(.+\)$/)) {
      // This is the full match — skip, use next parts
      i++;
      continue;
    }
    if (i + 2 < parts.length && parts[i + 1] && parts[i + 1].match(/^\[.+\]\(.+\)$/)) {
      // Text before
      if (parts[i]) elements.push(parts[i]);
      // The link
      const linkText = parts[i + 2];
      const linkUrl = parts[i + 3];
      const isDownload = linkText.includes("📥") || linkUrl.includes("/reports/");
      elements.push(
        React.createElement("a", {
          key: `link-${i}`,
          href: linkUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${linkUrl}` : linkUrl,
          target: "_blank",
          rel: "noopener noreferrer",
          style: {
            display: isDownload ? "inline-flex" : "inline",
            alignItems: "center",
            gap: 6,
            padding: isDownload ? "8px 16px" : "0",
            margin: isDownload ? "8px 0" : "0",
            background: isDownload ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
            color: isDownload ? "#fff" : "#667eea",
            borderRadius: isDownload ? 8 : 0,
            textDecoration: isDownload ? "none" : "underline",
            fontWeight: isDownload ? 600 : 400,
            fontSize: isDownload ? 13 : "inherit",
            boxShadow: isDownload ? "0 2px 8px rgba(102,126,234,0.3)" : "none",
          },
        }, linkText)
      );
      i += 4;
    } else {
      if (parts[i]) elements.push(parts[i]);
      i++;
    }
  }
  return elements;
};

const APP_VERSION = require("../../../package.json").version;
const CHAT_STORAGE_KEY = "aiAssistant_chat";
const CHAT_MAX_MESSAGES = 80;

// Histórico só é reaproveitado na mesma versão do sistema e para o mesmo usuário.
// Logout já limpa o localStorage inteiro (useAuth).
const loadChatCache = (userId) => {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    if (!cache || cache.version !== APP_VERSION || cache.userId !== (userId ?? null)) return null;
    return cache;
  } catch {
    return null;
  }
};

const clearChatCache = () => {
  try {
    localStorage.removeItem(CHAT_STORAGE_KEY);
  } catch {}
};

// **negrito** → <strong>; o restante passa pelo tratamento de links
const renderMessageContent = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*\n][^*\n]*?\*\*)/g);
  if (parts.length === 1) return renderLinks(text);
  return parts.map((part, idx) => {
    if (idx % 2 === 1) {
      return React.createElement("strong", { key: `b-${idx}` }, part.slice(2, -2));
    }
    return part
      ? React.createElement(React.Fragment, { key: `t-${idx}` }, renderLinks(part))
      : null;
  });
};

const AIAssistant = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadChatCache(user?.id)?.messages || []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(() => loadChatCache(user?.id)?.conversationId || null);
  const [images, setImages] = useState([]); // { base64, preview }
  const [planEnabled, setPlanEnabled] = useState(null); // null = not checked
  const pendingAutoMessage = useRef(null);
  const pageContext = routeToPageContext(location.pathname);

  // Auto-focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  // Escape key listener to close panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Auto-scroll on new messages AND when drawer reopens
  useEffect(() => {
    if (open) {
      // Pequeno delay para garantir que o DOM do drawer já renderizou
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages, loading, open]);

  // Persiste o histórico em localStorage (sobrevive à troca de página e a recarregar)
  useEffect(() => {
    try {
      if (messages.length === 0 && !conversationId) {
        localStorage.removeItem(CHAT_STORAGE_KEY);
        return;
      }
      const trimmed = messages.slice(-CHAT_MAX_MESSAGES).map(({ images, ...m }) => m);
      localStorage.setItem(
        CHAT_STORAGE_KEY,
        JSON.stringify({ version: APP_VERSION, userId: user?.id ?? null, conversationId, messages: trimmed })
      );
    } catch {}
  }, [messages, conversationId, user?.id]);

  // Check plan when opening
  const checkPlan = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-agent/check");
      setPlanEnabled(data.enabled);
    } catch (err) {
      setPlanEnabled(false);
    }
  }, []);

  // ─── Listen for "Criar Oportunidade via IA" event from MessageOptionsMenu ───
  useEffect(() => {
    const handleCreateOpportunityEvent = (e) => {
      const { messageBody, contactName, contactNumber, ticketId } = e.detail || {};

      const prompt = [
        "Analise minuciosamente a mensagem do lead abaixo para extrair o máximo de informações comerciais e criar a oportunidade correspondente:",
        "",
        "REGRAS DE EXTRAÇÃO E PREENCHIMENTO:",
        "1. Identificação do Tipo:",
        "   - Se a mensagem mencionar consórcio (ex: 'consórcio de carro', 'consórcio imobiliário', 'simulação de consórcio'), defina opportunity_type = 'consortium'.",
        "   - Identifique o segmento (consortium_segment): 'auto' (carro/veículo), 'imovel' (casa/terreno/apto), 'moto', 'pesado' (caminhão/trator), 'nautico', ou 'servico'.",
        "2. Valores e Prazos:",
        "   - Extraia o valor do crédito / carta de consórcio e converta para número em reais no campo credit_value (ex: '100 mil', 'R$ 100 mil', '100k' -> 100000.00).",
        "   - Extraia o prazo desejado em parcelas/meses no campo term_months (ex: '24 meses' -> 24).",
        "   - Extraia o valor da parcela mensal se houver no campo installment_value.",
        "   - Extraia a administradora se mencionada (ex: Porto Seguro, Ademicon, etc.) no campo administrator.",
        "3. Título e Descrição:",
        "   - Monte um título claro como: '<Nome do Lead> - Consórcio de Carro (100k)' ou equivalente.",
        "   - Na descrição/observações, inclua o resumo dos desejos do lead (ex: 'Simulação de consórcio de carro - Prazo: 24 meses. Deseja agendar consultoria.').",
        "4. Busca do Contato:",
        "   - Se não tiver contact_id prévio, busque o contato com contact_search pelo telefone ou nome.",
        "5. Chamada de Ferramenta:",
        "   - Execute a ferramenta opportunity_create passando TODOS os parâmetros estruturados extraídos (credit_value, term_months, consortium_segment, opportunity_type, etc.).",
        "",
        `Contato: ${contactName || "não identificado"}${contactNumber ? ` (${contactNumber})` : ""}`,
        ticketId ? `Ticket: #${ticketId}` : "",
        "",
        "Mensagem do lead:",
        "---",
        messageBody || "(sem conteúdo)",
        "---"
      ].filter(Boolean).join("\n");

      pendingAutoMessage.current = prompt;

      // Open the drawer — the auto-send fires once the drawer is open
      if (planEnabled === null) checkPlan();
      setOpen(true);
    };

    window.addEventListener("ai-create-opportunity-from-message", handleCreateOpportunityEvent);
    return () => {
      window.removeEventListener("ai-create-opportunity-from-message", handleCreateOpportunityEvent);
    };
  }, [planEnabled, checkPlan]);

  // Auto-send pending message when drawer opens
  useEffect(() => {
    if (!open || !pendingAutoMessage.current || loading) return;

    const msg = pendingAutoMessage.current;
    pendingAutoMessage.current = null;

    // Small delay to let the drawer render
    const timer = setTimeout(async () => {
      const ctx = pageContext || "tickets"; // fallback to tickets (opportunity_create lives there)

      const userMsg = {
        role: "user",
        content: msg,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setLoading(true);

      try {
        const { data } = await api.post("/ai-agent/chat", {
          message: msg,
          pageContext: ctx,
          conversationId,
        });

        setConversationId(data.conversationId);

        const assistantMsg = {
          role: "assistant",
          content: data.response,
          toolsExecuted: data.toolsExecuted,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, assistantMsg]);

        if (data.uiActions && data.uiActions.length > 0) {
          for (const action of data.uiActions) {
            if (action.type === "navigate" && action.url) {
              history.push(action.url);
            } else if (action.type === "reload") {
              window.location.reload();
            }
          }
        }
      } catch (err) {
        const errMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Erro ao processar a criação de oportunidade. Tente novamente.";

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `❌ ${errMsg}`,
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [open, loading, pageContext, conversationId, history]);



  const handleOpen = () => {
    if (planEnabled === null) checkPlan();
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setImages([]);
    clearChatCache();
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Max 3 images
    const remaining = 3 - images.length;
    const toProcess = files.slice(0, remaining);

    toProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1]; // remove data:image/...;base64,
        setImages((prev) => [
          ...prev,
          { base64, preview: reader.result, name: file.name },
        ]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePaste = (e) => {
    const clipboardItems = e.clipboardData?.items;
    if (!clipboardItems) return;

    const imageItems = Array.from(clipboardItems).filter((item) =>
      item.type.startsWith("image/")
    );
    if (imageItems.length === 0) return;

    e.preventDefault(); // impede colar texto da imagem
    const remaining = 3 - images.length;
    if (remaining <= 0) {
      toast.warning("Máximo de 3 imagens por mensagem.");
      return;
    }

    imageItems.slice(0, remaining).forEach((item) => {
      const file = item.getAsFile();
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setImages((prev) => [
          ...prev,
          { base64, preview: reader.result, name: `clipboard-${Date.now()}.png` },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed && images.length === 0) return;

    const userMsg = {
      role: "user",
      content: trimmed,
      images: images.map((i) => i.preview),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    const sentImages = images.map((i) => i.base64);
    setImages([]);
    setLoading(true);

    try {
      const { data } = await api.post("/ai-agent/chat", {
        message: trimmed || "(imagem enviada)",
        images: sentImages.length > 0 ? sentImages : undefined,
        pageContext,
        conversationId,
      });

      setConversationId(data.conversationId);

      const assistantMsg = {
        role: "assistant",
        content: data.response,
        toolsExecuted: data.toolsExecuted,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMsg]);

      // Processar uiActions retornadas pelo agente
      if (data.uiActions && data.uiActions.length > 0) {
        for (const action of data.uiActions) {
          switch (action.type) {
            case "navigate":
              if (action.url) {
                history.push(action.url);
              }
              break;
            case "navigate_and_draft":
              if (action.url) {
                history.push(action.url);
              }
              break;
            case "reload":
              window.location.reload();
              break;
            default:
              break;
          }
        }
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erro ao processar sua mensagem. Tente novamente.";

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `❌ ${errMsg}`,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* 1. Edge-Aligned Tab (Gatilho compacto na borda direita) */}
      <Tooltip title="Assistente IA" placement="left">
        <div
          className={`${classes.edgeTab} ${open ? classes.edgeTabOpen : ""}`}
          onClick={handleOpen}
        >
          <ChevronLeftIcon style={{ fontSize: 24 }} />
        </div>
      </Tooltip>

      {/* 3. Camada de Sobreposição (Backdrop) */}
      <div
        className={`${classes.backdrop} ${open ? classes.backdropOpen : ""}`}
        onClick={handleClose}
      />

      {/* 2. Painel Lateral (Off-Canvas Drawer) */}
      <div
        className={`${classes.offCanvasPanel} ${open ? classes.panelOpen : ""}`}
      >
        {/* Header */}
        <div className={classes.header}>
          <div>
            <Typography className={classes.headerTitle}>
              Assistente IA
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {pageLabels[pageContext] || "Sistema"}
            </Typography>
          </div>
          <div>
            <Tooltip title="Nova conversa">
              <IconButton
                size="small"
                style={{ color: "#fff", marginRight: 4 }}
                onClick={handleNewConversation}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              style={{ color: "#fff" }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {/* Messages */}
        <div className={classes.messagesContainer}>
          {planEnabled === false && (
            <Box className={classes.welcomeBox}>
              <Typography variant="body2" color="textSecondary">
                O assistente de IA não está habilitado no plano da sua empresa.
                Entre em contato com o suporte para ativar.
              </Typography>
            </Box>
          )}

          {planEnabled !== false && messages.length === 0 && (
            <Box className={classes.welcomeBox}>
              <EmojiObjectsIcon
                style={{ fontSize: 48, color: "#667eea", marginBottom: 12 }}
              />
              <Typography variant="body1" gutterBottom>
                Olá, {user?.name?.split(" ")[0] || ""}! 👋
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Sou o assistente do AtendeChatPro. Posso te ajudar com dúvidas,
                orientação e ações na página de <strong>{pageLabels[pageContext] || "Sistema"}</strong>.
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 8 }}
              >
                Envie um print se tiver algum erro!
              </Typography>
            </Box>
          )}

          {messages.map((msg, idx) => (
            <div key={idx}>
              <div
                className={`${classes.messageBubble} ${
                  msg.role === "user"
                    ? classes.userMessage
                    : classes.assistantMessage
                }`}
              >
                {renderMessageContent(msg.content)}
                {msg.images &&
                  msg.images.map((img, imgIdx) => (
                    <img
                      key={imgIdx}
                      src={img}
                      alt="screenshot"
                      className={classes.imagePreview}
                      style={{ display: "block", marginTop: 6 }}
                    />
                  ))}
              </div>
              {msg.toolsExecuted && msg.toolsExecuted.length > 0 && (
                <div style={{ marginTop: 4, marginLeft: 4 }}>
                  {msg.toolsExecuted.map((tool, tIdx) => (
                    <Chip
                      key={tIdx}
                      label={tool.replace(/_/g, " ")}
                      size="small"
                      variant="outlined"
                      className={classes.toolChip}
                      style={{ borderColor: "#667eea", color: "#667eea" }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className={classes.typingIndicator}>
              <div className={classes.dot} />
              <div className={classes.dot} />
              <div className={classes.dot} />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Image preview strip */}
        {images.length > 0 && (
          <Box
            display="flex"
            gap={1}
            px={2}
            py={1}
            style={{ borderTop: "1px solid #eee" }}
          >
            {images.map((img, idx) => (
              <Box key={idx} position="relative" display="inline-block">
                <img
                  src={img.preview}
                  alt={img.name}
                  style={{
                    width: 60,
                    height: 45,
                    borderRadius: 6,
                    objectFit: "cover",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeImage(idx)}
                  style={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    background: "#f44336",
                    color: "#fff",
                    width: 18,
                    height: 18,
                  }}
                >
                  <CloseIcon style={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Input */}
        <div className={classes.inputArea}>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImageUpload}
          />
          <Tooltip title="Enviar screenshot">
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || images.length >= 3 || planEnabled === false}
            >
              <ImageIcon />
            </IconButton>
          </Tooltip>

          <TextField
            className={classes.textField}
            variant="outlined"
            size="small"
            multiline
            maxRows={4}
            inputRef={inputRef}
            autoFocus
            placeholder="Digite sua mensagem..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            disabled={loading || planEnabled === false}
          />

          <IconButton
            className={classes.sendButton}
            onClick={handleSend}
            disabled={
              loading ||
              (!input.trim() && images.length === 0) ||
              planEnabled === false
            }
          >
            {loading ? <CircularProgress size={22} /> : <SendIcon />}
          </IconButton>
        </div>
      </div>
    </>
  );
};

export default AIAssistant;
